import { normalizeSearchText, toPersianDigits } from '@saay/formatting';

export interface LabeledValue {
  readonly disabled?: boolean;
  readonly label: string;
  readonly value: string;
}

export interface CityRow {
  readonly cityId: string;
  readonly cityName: string;
  readonly provinceId: string;
  readonly provinceName: string;
}

const CHIP_SPLIT = /[,،\s\n]+/u;

/** Splits pasted chip text on comma, Arabic comma, whitespace, and newlines (I10). */
export function splitChipPaste(text: string): readonly string[] {
  return text
    .split(CHIP_SPLIT)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function includesNormalized(haystack: string, needle: string): boolean {
  if (needle.length === 0) {
    return true;
  }
  return normalizeSearchText(haystack).includes(normalizeSearchText(needle));
}

/** Persian-normalized option filter for combobox and select menus (S6, S7). */
export function filterLabeledOptions<T extends LabeledValue>(
  options: readonly T[],
  query: string,
): readonly T[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return options;
  }
  return options.filter((option) => includesNormalized(option.label, trimmed));
}

export type CityListEntry =
  | { readonly kind: 'group'; readonly provinceName: string }
  | { readonly kind: 'city'; readonly row: CityRow };

/**
 * City combobox filter (D-76, Divar-style): typing a province also lists its cities; city rows show
 * «شهر، استان».
 */
export function filterCityList(rows: readonly CityRow[], query: string): readonly CityListEntry[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return rows.map((row) => ({ kind: 'city', row }));
  }
  const matchedProvinces = new Set<string>();
  for (const row of rows) {
    if (includesNormalized(row.provinceName, trimmed)) {
      matchedProvinces.add(row.provinceId);
    }
  }
  const out: CityListEntry[] = [];
  let lastGroup: string | undefined;
  for (const row of rows) {
    const cityMatch = includesNormalized(row.cityName, trimmed);
    const provinceMatch = matchedProvinces.has(row.provinceId);
    if (!cityMatch && !provinceMatch) {
      continue;
    }
    if (provinceMatch && row.provinceName !== lastGroup) {
      out.push({ kind: 'group', provinceName: row.provinceName });
      lastGroup = row.provinceName;
    }
    out.push({ kind: 'city', row });
  }
  return out;
}

export function formatCityLabel(row: CityRow): string {
  return `${row.cityName}، ${row.provinceName}`;
}

export function chipCounterLabel(count: number, max: number): string {
  return `${toPersianDigits(String(count))}/${toPersianDigits(String(max))}`;
}
